import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@veracity/vui'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { scenariosClient } from '@/api'
import {
  CloneScenarioRequest,
  ScenarioTags,
  UpdatePropertyRequest
} from '@/interfaces'

export const useScenarioDetails = (projectId: number, scenarioId: number) => {
  const QUERY_KEY = scenariosClient.QUERY_KEY(scenarioId)

  const navigate = useNavigate()
  const { showSuccess, showError } = useToast()
  const queryClient = useQueryClient()

  const getScenarioDetailsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => scenariosClient.getById(scenarioId),
    enabled: !!scenarioId
  })

  useEffect(() => {
    if (!getScenarioDetailsQuery.data?.projectId) return

    if (getScenarioDetailsQuery.data.projectId !== projectId) {
      showError('Scenario with given id not exists')
      navigate(`/projects/${projectId}`)
    }
  }, [getScenarioDetailsQuery.data?.projectId, navigate, projectId, showError])

  const updateScenarioMutation = useMutation({
    mutationFn: (scenarioName: string) =>
      scenariosClient.updateName(scenarioId, scenarioName),
    onSuccess: async () => {
      showSuccess('Scenario successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const removeScenarioMutation = useMutation({
    mutationFn: () => scenariosClient.removeById(scenarioId),
    onSuccess: () => {
      showSuccess('Scenario successfully removed')
      if (getScenarioDetailsQuery.data?.projectId)
        navigate(`/projects/${getScenarioDetailsQuery.data.projectId}`)
    }
  })

  const cloneScenarioMutation = useMutation({
    mutationFn: (req: CloneScenarioRequest) =>
      scenariosClient.clone(scenarioId, req),
    onSuccess: (clonedScenarioId: number) => {
      showSuccess('Scenario successfully cloned')
      if (getScenarioDetailsQuery.data?.projectId)
        navigate(
          `/projects/${getScenarioDetailsQuery.data?.projectId}/scenarios/${clonedScenarioId}`
        )
    }
  })

  const updateTagMutation = useMutation({
    mutationFn: (tag: ScenarioTags) =>
      scenariosClient.updateTag(scenarioId, tag),
    onSuccess: async () => {
      showSuccess('Scenario successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const updateScenarioFieldMutation = useMutation({
    mutationFn: (req: UpdatePropertyRequest) =>
      scenariosClient.patchScenarioField(scenarioId, req),
    onSuccess: async () => {
      showSuccess('Scenario successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const runCalculationMutation = useMutation({
    mutationFn: () => scenariosClient.runCalculation(scenarioId),
    onSuccess: async () => {
      showSuccess('Calculation successfully started')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const cancelCalculationMutation = useMutation({
    mutationFn: () => scenariosClient.cancelCalculation(scenarioId),
    onSuccess: async () => {
      showSuccess('Calculation successfully aborted')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  return {
    scenarioInfo: getScenarioDetailsQuery.data,
    isFetching:
      getScenarioDetailsQuery.isFetching &&
      !getScenarioDetailsQuery.isRefetching,
    updateScenarioName: updateScenarioMutation.mutate,
    isUpdateScenarioNameInProgress: updateScenarioMutation.isPending,
    removeScenario: removeScenarioMutation.mutate,
    isRemoveScenarioInProgress: removeScenarioMutation.isPending,
    cloneScenario: cloneScenarioMutation.mutate,
    isCloneScenarioInProgress: cloneScenarioMutation.isPending,
    updateScenarioTag: updateTagMutation.mutate,
    isUpdateScenarioTagInProgress: updateTagMutation.isPending,
    updateScenarioField: updateScenarioFieldMutation.mutate,
    runCalculation: runCalculationMutation.mutate,
    runCalculationInProgress: runCalculationMutation.isPending,
    cancelCalculation: cancelCalculationMutation.mutate,
    cancelCalculationInProgress: cancelCalculationMutation.isPending
  }
}
